import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FireIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { scenarioService, type MyScenario, type ScenarioGroupInfo } from '../../services/scenario';
import ScenarioAppIcon from './ScenarioAppIcon';
import SidebarToggleIcon from '../icons/SidebarToggleIcon';
import ComposeIcon from '../icons/ComposeIcon';
import ScenarioDetailDialog from './ScenarioDetailDialog';
import WindowTitleBar from '../window/WindowTitleBar';

/**
 * 场景应用首页（与桌面端 390ea165 重构版同构）：
 *   精选场景（按使用频率前 4，彩色大卡）→ 全部场景（分组 tab + 宽卡 + 分页）。
 * web 差异：数据经服务端代理按 lang 返回本地化文案（无需本地映射表）；
 * 空态区分"真没有场景"与"平台登录态失效"（scenarioService.authFailed）。
 */

const PAGE_SIZE = 9;
const SCENARIO_PAGE_VARIANTS = {
  enter: (direction: number) => ({ x: direction * 96, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction * -96, opacity: 0 }),
};

/** 卡片只决定头像尺寸与裁剪形状，图片来源及失败回退统一由共享组件处理。 */
const ScenarioIconRenderer: React.FC<{
  scenario: MyScenario;
  size?: 'sm' | 'md' | 'lg' | 'hero';
}> = ({ scenario, size = 'md' }) => (
  <ScenarioAppIcon
    scenario={scenario}
    size={size === 'hero' ? 96 : size === 'lg' ? 48 : size === 'sm' ? 20 : 24}
    className={size === 'lg' ? 'rounded-full' : 'rounded-md'}
  />
);

const ScenarioTags: React.FC<{ tags: string }> = ({ tags }) => {
  const tagList = tags.split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 2);
  if (tagList.length === 0) return null;

  return (
    <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
      {tagList.map(tag => (
        <span
          key={tag}
          className="max-w-28 truncate rounded-md bg-claude-surfaceMuted px-2 py-0.5 text-[10px] font-medium text-claude-textSecondary dark:bg-claude-darkSurfaceHover dark:text-claude-darkTextSecondary"
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

interface ScenarioCardProps {
  scenario: MyScenario;
  entering: boolean;
  hasLayoutId: boolean;
  onStart: (scenario: MyScenario) => void;
  hotLabel: string;
  recommendedLabel: string;
}

const ScenarioCard = React.memo<ScenarioCardProps>(({
  scenario,
  entering,
  hasLayoutId,
  onStart,
  hotLabel,
  recommendedLabel,
}) => {
  const expertName = scenario.expert_name?.trim();
  const subtitle = expertName && expertName !== scenario.group_name
    ? `${scenario.group_name} · ${expertName}`
    : scenario.group_name;

  return (
    <motion.button
      type="button"
      layoutId={hasLayoutId ? `scenario-card-${scenario.id}` : undefined}
      onClick={() => onStart(scenario)}
      className="group relative z-0 flex h-[174px] min-w-0 flex-col overflow-hidden rounded-2xl border border-claude-borderLight bg-claude-surface p-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.035)] outline-none transition-[border-color,box-shadow,background-color] hover:z-10 hover:border-claude-border hover:bg-white hover:shadow-[0_12px_30px_rgba(15,23,42,0.1)] focus-visible:ring-2 focus-visible:ring-claude-accent/40 dark:border-claude-darkBorderLight dark:bg-claude-darkSurface dark:hover:border-claude-darkBorder dark:hover:bg-claude-darkSurface"
      animate={entering ? { scale: [1, 0.985, 1.015, 1] } : { scale: 1 }}
      whileHover={{
        scale: 1.015,
        y: -2,
        transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
      }}
      transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
      style={hasLayoutId || entering ? { willChange: 'transform' } : undefined}
    >
      <div className="flex w-full items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/[0.06] bg-claude-surfaceMuted dark:border-white/10 dark:bg-claude-darkSurfaceHover">
          <ScenarioIconRenderer scenario={scenario} size="lg" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-[15px] font-semibold text-claude-text dark:text-claude-darkText">
              {scenario.name}
            </h3>
            {scenario.is_hot && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 dark:bg-rose-500/10 dark:text-rose-300" title={hotLabel}>
                <FireIcon className="h-3 w-3" />
                {hotLabel}
              </span>
            )}
            {!scenario.is_hot && scenario.is_recommended && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300" title={recommendedLabel}>
                <SparklesIcon className="h-3 w-3" />
                {recommendedLabel}
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-[11px] font-medium text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {subtitle}
          </p>
        </div>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-claude-borderLight bg-claude-surface text-claude-textSecondary shadow-sm transition-colors group-hover:border-claude-border group-hover:bg-claude-surfaceHover group-hover:text-claude-text dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkTextSecondary dark:group-hover:border-claude-darkBorder dark:group-hover:bg-claude-darkSurfaceHover dark:group-hover:text-claude-darkText">
          <ArrowUpRightIcon className="h-4 w-4" />
        </span>
      </div>

      {scenario.description && (
        <p className="mt-3 line-clamp-2 text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {scenario.description}
        </p>
      )}

      <div className="mt-auto flex w-full items-center justify-between gap-3 pt-2">
        <ScenarioTags tags={scenario.tags || ''} />
        {scenario.usage_count > 0 && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[10px] tabular-nums text-claude-textSecondary dark:text-claude-darkTextSecondary">
            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" />
            {scenario.usage_count}
          </span>
        )}
      </div>
    </motion.button>
  );
});
ScenarioCard.displayName = 'ScenarioCard';

const FEATURE_ACCENT_BY_SCENARIO_NAME: Record<string, string> = {
  '自媒体助手': '84, 190, 232',
  '行业分析专家': '100, 202, 145',
  'PC电脑助手': '247, 194, 69',
  '市场流动性分析报告': '241, 124, 132',
  '视频创意': '148, 132, 199',
  '钢铁产业情报': '64, 147, 184',
  '学习助手': '238, 175, 69',
  '投资建模与专业交付专家': '43, 190, 199',
  'BoMate全生命周期投资总控专家': '67, 132, 205',
};
const FEATURE_ACCENT_FALLBACKS = [
  '84, 190, 232',
  '100, 202, 145',
  '247, 194, 69',
  '241, 124, 132',
];

const PopularScenarioCard: React.FC<{
  scenario: MyScenario;
  rank: number;
  onStart: (scenario: MyScenario) => void;
}> = ({ scenario, rank, onStart }) => {
  const accent = FEATURE_ACCENT_BY_SCENARIO_NAME[scenario.name?.trim()]
    || FEATURE_ACCENT_FALLBACKS[rank % FEATURE_ACCENT_FALLBACKS.length];

  return (
    <button
      type="button"
      onClick={() => onStart(scenario)}
      className="group relative z-0 flex h-[170px] min-w-0 flex-col overflow-hidden rounded-2xl border border-claude-borderLight bg-claude-surface p-5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.035)] outline-none transition-[border-color,box-shadow,transform,background-color] duration-200 hover:z-10 hover:-translate-y-0.5 hover:scale-[1.015] hover:border-claude-border hover:shadow-[0_12px_30px_rgba(15,23,42,0.1)] focus-visible:ring-2 focus-visible:ring-claude-accent/40 dark:border-white/[0.07] dark:bg-[#1f1f1f] dark:hover:border-white/[0.12] dark:hover:bg-[#202020] dark:hover:shadow-[0_14px_34px_rgba(0,0,0,0.32)]"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 dark:hidden"
          style={{ background: `linear-gradient(105deg, rgba(${accent}, 0.16) 0%, rgba(${accent}, 0.09) 58%, rgba(${accent}, 0.02) 100%)` }}
        />
        <div
          className="absolute inset-0 hidden dark:block"
          style={{ background: `linear-gradient(90deg, #1f1f1f 0%, rgba(${accent}, 0.04) 34%, rgba(${accent}, 0.14) 68%, rgba(${accent}, 0.28) 100%)` }}
        />
        <div className="absolute inset-y-0 -right-2 flex w-[62%] items-start justify-end pt-4 pr-6 opacity-[0.18] transition-transform duration-500 ease-out group-hover:scale-[1.035]">
          <ScenarioIconRenderer scenario={scenario} size="hero" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/14 to-white/58 dark:hidden" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/34 via-white/18 to-transparent dark:hidden" />
        <div className="absolute inset-y-0 left-0 hidden w-[62%] bg-gradient-to-r from-[#1f1f1f]/95 via-[#1f1f1f]/70 to-transparent dark:block" />
        <div className="absolute inset-0 hidden bg-gradient-to-b from-transparent via-transparent to-[#1f1f1f]/88 dark:block" />
      </div>
      <div className="relative z-10 flex items-center gap-2">
        <span className="text-[10px] font-bold tabular-nums text-claude-textSecondary/80 dark:text-claude-darkTextSecondary/80">
          {String(rank + 1).padStart(2, '0')}
        </span>
        <span className="truncate text-[11px] font-medium text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {scenario.group_name}
        </span>
      </div>
      <div className="relative z-10 mt-4 min-w-0 pr-[40%]">
        <h3 className="line-clamp-2 text-base font-semibold leading-6 text-claude-text dark:text-claude-darkText">
          {scenario.name}
        </h3>
        {scenario.description && (
          <p className="mt-1 line-clamp-1 text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {scenario.description}
          </p>
        )}
      </div>
      <div className="relative z-10 mt-auto flex items-center justify-between gap-3">
        <p className="truncate text-[11px] font-medium text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {scenario.expert_name || scenario.group_name}
        </p>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/70 text-claude-text shadow-sm backdrop-blur-sm dark:bg-white/[0.08] dark:text-claude-darkText dark:shadow-none dark:ring-1 dark:ring-white/[0.06]">
          <ArrowUpRightIcon className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
};

interface ScenariosViewProps {
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
  onStartScenarioChat: (scenario: MyScenario) => void | boolean | Promise<void | boolean>;
  enteringScenarioId?: number | null;
  layoutCardId?: number | null;
}

const ScenariosView: React.FC<ScenariosViewProps> = ({
  isSidebarCollapsed,
  onToggleSidebar,
  onNewChat,
  onStartScenarioChat,
  enteringScenarioId,
  layoutCardId,
}) => {
  const isMac = window.electron.platform === 'darwin';
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState<MyScenario[]>(scenarioService.getScenarios());
  const [allGroups, setAllGroups] = useState<ScenarioGroupInfo[]>(scenarioService.getGroups());
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScenario, setSelectedScenario] = useState<MyScenario | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDirection, setPageDirection] = useState<1 | -1>(1);

  useEffect(() => {
    const unsubscribe = scenarioService.subscribe(() => {
      setScenarios(scenarioService.getScenarios());
      setAllGroups(scenarioService.getGroups());
      setLoading(scenarioService.isLoading);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setLoading(true);
    void scenarioService.loadScenarios();
  }, []);

  // 语言切换后重新拉取（服务端按 lang 返回本地化文案）
  useEffect(() => i18nService.subscribe(() => {
    setActiveTab(null);
    void scenarioService.loadScenarios();
  }), []);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    void scenarioService.loadScenarios();
  }, []);

  const groups = useMemo(() => {
    const countMap = new Map<number, number>();
    for (const scenario of scenarios) {
      countMap.set(scenario.group_id, (countMap.get(scenario.group_id) || 0) + 1);
    }

    if (allGroups.length > 0) {
      return allGroups.map(group => ({
        group_id: group.id,
        group_name: group.name,
        count: countMap.get(group.id) || 0,
      }));
    }

    const groupMap = new Map<number, { group_id: number; group_name: string; count: number }>();
    for (const scenario of scenarios) {
      if (!groupMap.has(scenario.group_id)) {
        groupMap.set(scenario.group_id, {
          group_id: scenario.group_id,
          group_name: scenario.group_name,
          count: 0,
        });
      }
      groupMap.get(scenario.group_id)!.count += 1;
    }
    return Array.from(groupMap.values());
  }, [scenarios, allGroups]);

  const filteredScenarios = useMemo(() => {
    let result = scenarios;
    if (activeTab !== null) {
      result = result.filter(scenario => scenario.group_id === activeTab);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((scenario) => (
        scenario.name.toLowerCase().includes(query)
        || scenario.description?.toLowerCase().includes(query)
        || scenario.tags?.toLowerCase().includes(query)
        || scenario.expert_name?.toLowerCase().includes(query)
      ));
    }
    return result;
  }, [scenarios, activeTab, searchQuery]);

  useEffect(() => {
    setPageDirection(-1);
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredScenarios.length / PAGE_SIZE));
  const handlePageChange = useCallback((nextPage: number) => {
    const boundedPage = Math.min(totalPages, Math.max(1, nextPage));
    if (boundedPage === currentPage) return;
    setPageDirection(boundedPage > currentPage ? 1 : -1);
    setCurrentPage(boundedPage);
  }, [currentPage, totalPages]);
  const pagedScenarios = filteredScenarios.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const ranking = useMemo(() => (
    [...scenarios]
      .sort((first, second) => second.usage_count - first.usage_count)
      .slice(0, 4)
  ), [scenarios]);

  return (
    <MotionConfig reducedMotion="user">
    <div className="relative flex h-full flex-1 flex-col bg-[#FAFAFB] dark:bg-claude-darkBg">
      {selectedScenario && <ScenarioDetailDialog key={selectedScenario.id} scenario={selectedScenario} onClose={() => setSelectedScenario(null)} onUse={onStartScenarioChat} />}
      <div className="draggable flex h-12 shrink-0 items-center justify-between px-4">
        <div className="flex h-8 items-center gap-3">
          {isSidebarCollapsed && (
            <div className={`non-draggable flex items-center gap-1 ${isMac ? 'pl-[68px]' : ''}`}>
              <button
                type="button"
                onClick={onToggleSidebar}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover"
                title="Sidebar"
              >
                <SidebarToggleIcon className="h-4 w-4" isCollapsed />
              </button>
              <button
                type="button"
                onClick={onNewChat}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover"
                title="New chat"
              >
                <ComposeIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          <h1 className="text-lg font-semibold text-claude-text dark:text-claude-darkText">
            {i18nService.t('scenariosTitle')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {scenarios.length > 0 && (
            <div className="non-draggable relative w-64 2xl:w-72">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
              <input
                type="text"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder={i18nService.t('scenariosSearchPlaceholder')}
                className="h-9 w-full rounded-lg border border-claude-border bg-claude-surface pl-9 pr-9 text-xs text-claude-text outline-none transition-[border-color,box-shadow] placeholder:text-claude-textSecondary focus:border-claude-accent/60 focus:ring-2 focus:ring-claude-accent/10 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:placeholder:text-claude-darkTextSecondary"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                  title={i18nService.t('scenariosSearchPlaceholder')}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="non-draggable inline-flex h-8 w-8 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:opacity-50 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
            title={i18nService.t('scenariosRefresh')}
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <WindowTitleBar inline />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1420px] px-5 py-5 2xl:px-7">
          {loading && scenarios.length === 0 ? (
            <div className="space-y-6 animate-pulse">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[1, 2, 3, 4].map(item => (
                  <div key={item} className="h-[170px] rounded-2xl border border-claude-border bg-claude-surface p-3 dark:border-claude-darkBorder dark:bg-claude-darkSurface" />
                ))}
              </div>
              <div className="h-11 rounded-lg bg-claude-surfaceMuted dark:bg-claude-darkSurfaceHover" />
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(item => (
                  <div key={item} className="h-[182px] rounded-2xl border border-claude-border bg-claude-surface dark:border-claude-darkBorder dark:bg-claude-darkSurface" />
                ))}
              </div>
            </div>
          ) : scenarioService.loadFailed ? (
            <div role="alert" className="flex min-h-[480px] flex-col items-center justify-center gap-3 text-sm text-claude-text dark:text-claude-darkText">
              <p>{i18nService.t('scenariosLoadFailed')}</p>
              <button type="button" onClick={handleRefresh} className="rounded-lg border border-claude-border px-4 py-2 hover:bg-claude-surface dark:border-claude-darkBorder dark:hover:bg-claude-darkSurface">
                {i18nService.t('retry')}
              </button>
            </div>
          ) : scenarios.length === 0 ? (
            <div className="flex min-h-[480px] flex-col items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-claude-border bg-claude-surface text-claude-textSecondary dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkTextSecondary">
                <Squares2X2Icon className="h-6 w-6" />
              </div>
              <p className="mt-4 text-sm font-medium text-claude-text dark:text-claude-darkText">
                {i18nService.t(scenarioService.authFailed ? 'scenariosAuthFailed' : 'scenariosEmpty')}
              </p>
              <p className="mt-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                {i18nService.t(scenarioService.authFailed ? 'scenariosAuthFailedHint' : 'scenariosEmptyHint')}
              </p>
            </div>
          ) : (
            <>
              {ranking.length > 0 && (
                <section aria-labelledby="popular-scenarios-title">
                  <div className="mb-3 flex items-end justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <SparklesIcon className="h-4 w-4 text-amber-500" />
                        <h2 id="popular-scenarios-title" className="text-base font-semibold text-claude-text dark:text-claude-darkText">
                          {i18nService.t('scenariosFeaturedTitle')}
                        </h2>
                      </div>
                      <p className="mt-1 text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                        {i18nService.t('scenariosRankingHint')}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {ranking.map((scenario, index) => (
                      <PopularScenarioCard
                        key={scenario.id}
                        scenario={scenario}
                        rank={index}
                        onStart={setSelectedScenario}
                      />
                    ))}
                  </div>
                </section>
              )}

              <section className="mt-7" aria-labelledby="scenario-catalog-title">
                <div className="mb-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <h2 id="scenario-catalog-title" className="text-base font-semibold text-claude-text dark:text-claude-darkText">
                      {i18nService.t('scenariosBrowseTitle')}
                    </h2>
                    <span className="text-xs tabular-nums text-claude-textSecondary dark:text-claude-darkTextSecondary">
                      {filteredScenarios.length}
                    </span>
                  </div>
                </div>
                <div className="border-b border-claude-borderLight dark:border-claude-darkBorderLight">
                  <div className="flex min-w-0 items-end pb-3">
                    <div className="min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <div className="flex min-w-max items-center gap-5">
                        <button
                          type="button"
                          onClick={() => setActiveTab(null)}
                          className={`relative h-8 text-xs font-medium transition-colors ${
                            activeTab === null
                              ? 'text-claude-text dark:text-claude-darkText'
                              : 'text-claude-textSecondary hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:text-claude-darkText'
                          }`}
                        >
                          {i18nService.t('scenariosAll')}
                          <span className="ml-1 tabular-nums opacity-60">{scenarios.length}</span>
                          {activeTab === null && <span className="absolute inset-x-0 -bottom-[13px] h-0.5 bg-claude-accent" />}
                        </button>
                        {groups.map(group => (
                          <button
                            key={group.group_id}
                            type="button"
                            onClick={() => setActiveTab(group.group_id)}
                            className={`relative h-8 text-xs font-medium transition-colors ${
                              activeTab === group.group_id
                                ? 'text-claude-text dark:text-claude-darkText'
                                : 'text-claude-textSecondary hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:text-claude-darkText'
                            }`}
                          >
                            {group.group_name}
                            <span className="ml-1 tabular-nums opacity-60">{group.count}</span>
                            {activeTab === group.group_id && <span className="absolute inset-x-0 -bottom-[13px] h-0.5 bg-claude-accent" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-4" aria-label={i18nService.t('scenariosTitle')}>
                {filteredScenarios.length === 0 ? (
                  <div className="flex min-h-[280px] flex-col items-center justify-center">
                    <MagnifyingGlassIcon className="h-6 w-6 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
                    <p className="mt-3 text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
                      {i18nService.t('scenariosNoResults')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="relative -mx-2 -my-2 overflow-hidden px-2 py-2">
                      <AnimatePresence initial={false} mode="popLayout" custom={pageDirection}>
                        <motion.div
                          key={currentPage}
                          custom={pageDirection}
                          variants={SCENARIO_PAGE_VARIANTS}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                          className="grid grid-cols-2 gap-3 xl:grid-cols-3"
                        >
                          {pagedScenarios.map(scenario => (
                            <ScenarioCard
                              key={scenario.id}
                              scenario={scenario}
                              entering={enteringScenarioId === scenario.id}
                              hasLayoutId={layoutCardId === scenario.id}
                              onStart={setSelectedScenario}
                              hotLabel={i18nService.t('scenariosHotBadge')}
                              recommendedLabel={i18nService.t('scenariosRecommendedBadge')}
                            />
                          ))}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {totalPages > 1 && (
                      <nav className="mt-5 flex items-center justify-center gap-1" aria-label="Pagination">
                        <button
                          type="button"
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          className="flex h-8 w-8 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:cursor-not-allowed disabled:opacity-25 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                          title={i18nService.t('scenariosPreviousPage')}
                        >
                          <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                          <button
                            key={page}
                            type="button"
                            onClick={() => handlePageChange(page)}
                            className={`h-8 min-w-8 rounded-md px-2 text-xs font-medium tabular-nums transition-colors ${
                              page === currentPage
                                ? 'bg-claude-text text-white dark:bg-claude-darkText dark:text-claude-darkBg'
                                : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          type="button"
                          disabled={currentPage >= totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                          className="flex h-8 w-8 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text disabled:cursor-not-allowed disabled:opacity-25 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                          title={i18nService.t('scenariosNextPage')}
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </nav>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
    </MotionConfig>
  );
};

export default ScenariosView;
